import type { TFunction } from 'i18next';
import type {
  CaptureSuggestionDto,
  SuggestionKind,
  SuggestionPayloadByKind,
  SuggestionRecurrence,
} from '../../types/capture';
import { parameterLabel } from '../measurements/measurementHistoryUtils';

/** i18n key segment of each kind's text badge (voice.kind.*). */
const KIND_KEYS: Readonly<Record<SuggestionKind, string>> = {
  journal_entry: 'journalEntry',
  task_create: 'taskCreate',
  task_recurring: 'taskRecurring',
  task_complete: 'taskComplete',
  measurement: 'measurement',
  water_change: 'waterChange',
  maintenance: 'maintenance',
  dosing: 'dosing',
  livestock_added: 'livestockAdded',
  livestock_lost: 'livestockLost',
  incident: 'incident',
  expense: 'expense',
  restock: 'restock',
  dosing_change: 'dosingChange',
};

export function kindLabel(kind: SuggestionKind, t: TFunction): string {
  return String(t(`voice.kind.${KIND_KEYS[kind]}`));
}

/** Kinds that belong to the "Records" section of the review card. */
export const RECORD_KINDS: ReadonlySet<SuggestionKind> = new Set([
  'measurement',
  'water_change',
  'maintenance',
  'dosing',
  'livestock_added',
  'livestock_lost',
  'incident',
  'expense',
  'dosing_change',
]);
/** Kinds of the collapsed "Tasks · N" disclosure (a restock is saved as a task). */
export const TASK_KINDS: ReadonlySet<SuggestionKind> = new Set(['task_create', 'task_recurring', 'task_complete', 'restock']);
/**
 * A capture whose open suggestions are only of these kinds renders the compact
 * card (persona review): rows + save, the rest behind "More".
 */
export const COMPACT_KINDS: ReadonlySet<SuggestionKind> = new Set([
  'measurement',
  'dosing',
  'maintenance',
  'water_change',
  'task_complete',
]);

/** 'YYYY-MM-DD' → a medium local date ('YYYY-MM-DD' is read as a calendar day, not UTC midnight). */
export function formatDay(day: string, language: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(date);
}

export function formatDateTime(iso: string, language: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

/** An expense amount in the user's budget currency (never converted). */
export function formatMoney(amount: number, currency: string, language: string): string {
  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency }).format(amount);
  } catch {
    return `${formatNumber(amount, language)} ${currency}`;
  }
}

export function formatNumber(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 3 }).format(value);
}

/** Whole days between an ISO instant and now (≥ 0). */
export function daysSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 0;
  return Math.max(0, Math.floor((now.getTime() - then) / 86_400_000));
}

const WEEKDAY_REFERENCE_SUNDAY = new Date(2024, 0, 7); // a Sunday

function weekdayName(day: number, language: string): string {
  const date = new Date(WEEKDAY_REFERENCE_SUNDAY);
  date.setDate(date.getDate() + day);
  return new Intl.DateTimeFormat(language, { weekday: 'long' }).format(date);
}

export function recurrenceSummary(rule: SuggestionRecurrence, t: TFunction, language: string): string {
  const interval = rule.interval ?? 1;
  switch (rule.frequency) {
    case 'daily':
      return t('voice.recurrence.daily');
    case 'every_x_days':
      return t('voice.recurrence.every_x_days', { count: interval });
    case 'weekly':
      return interval > 1 ? t('voice.recurrence.weeklyInterval', { count: interval }) : t('voice.recurrence.weekly');
    case 'monthly':
      return interval > 1 ? t('voice.recurrence.monthlyInterval', { count: interval }) : t('voice.recurrence.monthly');
    case 'specific_weekdays': {
      const days = (rule.weekdays ?? []).map((d) => weekdayName(d, language));
      return t('voice.recurrence.specific_weekdays', {
        days: new Intl.ListFormat(language, { type: 'conjunction' }).format(days),
      });
    }
    default:
      return '';
  }
}

/** The text of a suggestion row (its checkbox label). */
export function suggestionText<K extends SuggestionKind>(
  kind: K,
  payload: SuggestionPayloadByKind[K],
  t: TFunction,
  language: string,
): string {
  switch (kind) {
    case 'measurement': {
      const p = payload as SuggestionPayloadByKind['measurement'];
      return t('voice.suggestion.measurementItem', {
        parameter: parameterLabel(t, p.parameterId, p.parameterName),
        value: formatNumber(p.value, language),
        unit: p.unit,
      });
    }
    case 'water_change': {
      const p = payload as SuggestionPayloadByKind['water_change'];
      return p.volumeLiters !== null
        ? t('voice.suggestion.waterChangeItem', { volume: formatNumber(p.volumeLiters, language) })
        : t('voice.suggestion.waterChangeItemNoVolume');
    }
    case 'maintenance':
      return (payload as SuggestionPayloadByKind['maintenance']).note;
    case 'dosing': {
      const p = payload as SuggestionPayloadByKind['dosing'];
      return t('voice.suggestion.dosingItem', { product: p.productName, amount: formatNumber(p.amount, language), unit: p.unit });
    }
    case 'task_complete': {
      const p = payload as SuggestionPayloadByKind['task_complete'];
      const chosen = p.candidates.find((c) => c.ref === p.taskRef);
      return t('voice.suggestion.completeItem', { title: chosen?.title ?? p.title });
    }
    case 'livestock_added':
      return t('voice.suggestion.livestockAddedItem', { name: (payload as SuggestionPayloadByKind['livestock_added']).commonName });
    case 'livestock_lost': {
      const p = payload as SuggestionPayloadByKind['livestock_lost'];
      const chosen = p.candidates.find((c) => c.livestockId === p.livestockId);
      return t('voice.suggestion.livestockLostItem', { name: chosen?.commonName ?? p.commonName });
    }
    case 'incident':
      return t('voice.suggestion.incidentItem', { note: (payload as SuggestionPayloadByKind['incident']).note });
    case 'expense': {
      const p = payload as SuggestionPayloadByKind['expense'];
      return t('voice.suggestion.expenseItem', {
        amount: formatMoney(p.amount, p.currency, language),
        category: t(`budget.categories.${p.category}`),
      });
    }
    case 'dosing_change': {
      const p = payload as SuggestionPayloadByKind['dosing_change'];
      const to = formatNumber(p.newAmount, language);
      return p.currentAmount !== null
        ? t('voice.suggestion.dosingChangeItem', { product: p.productName, from: formatNumber(p.currentAmount, language), to, unit: p.unit })
        : t('voice.suggestion.dosingChangeItemNoCurrent', { product: p.productName, to, unit: p.unit });
    }
    case 'restock':
      return t('voice.suggestion.restockItem', { item: (payload as SuggestionPayloadByKind['restock']).item });
    case 'task_create':
    case 'task_recurring':
      return (payload as SuggestionPayloadByKind['task_create']).title;
    case 'journal_entry':
      return (payload as SuggestionPayloadByKind['journal_entry']).title;
    default:
      return '';
  }
}

/** The open (still to review) suggestions of a list. */
export function openSuggestions(suggestions: readonly CaptureSuggestionDto[]): CaptureSuggestionDto[] {
  return suggestions.filter((s) => s.status === 'suggested');
}
